import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { getSportLabel } from "@/lib/sport-defaults";

interface SportsDistributionPieProps {
  data: Array<{ sport: string; count: number }>;
}

const PALETTE = [
  "#6366f1", // indigo
  "#22c55e", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#a855f7", // purple
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#3b82f6", // blue
];

export function SportsDistributionPie({ data }: SportsDistributionPieProps) {
  if (!data.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No lockers assigned yet
      </div>
    );
  }

  const total = data.reduce((sum, d) => sum + d.count, 0);
  let paletteIdx = 0;
  const withColors = data.map((d) => {
    const isUnassigned = d.sport === "unassigned";
    const color = isUnassigned ? "#9ca3af" : PALETTE[paletteIdx++ % PALETTE.length]!;
    return { ...d, color, name: isUnassigned ? "Unassigned" : getSportLabel(d.sport) };
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
          <PieChart>
            <Pie
              data={withColors}
              dataKey="count"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="45%"
              outerRadius="80%"
              paddingAngle={2}
              stroke="var(--background)"
              strokeWidth={2}
            >
              {withColors.map((entry, idx) => (
                <Cell key={idx} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "var(--popover)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 12,
              }}
              formatter={(value: number, name: string) => [
                `${value} (${Math.round((value / total) * 100)}%)`,
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-3 space-y-1 text-xs max-h-32 overflow-auto">
        {withColors.map((entry) => (
          <li key={entry.sport} className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span
                className="h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="truncate">{entry.name}</span>
            </span>
            <span className="text-muted-foreground tabular-nums">
              {entry.count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
